/**
 * @fileoverview TanStack table `meta` accessor for the Day-One columns.
 *
 * Split out from `columns.tsx` / `columns.cells.tsx`
 * (react-refresh/only-export-components) — a plain value helper shared by
 * both the column defs and their cell/header renderers.
 */

import type { Table as TanStackTable } from "@tanstack/react-table";
import type { Id } from "@cvx/_generated/dataModel";
import type { DayOnePrecedentRow } from "./types";

/**
 * Side-channel callbacks injected via TanStack table `meta`. Lets each
 * column trigger row-level actions (preview, delete, Morgan draft)
 * without requiring the table to thread props through every cell.
 */
export interface DayOneTableMeta {
  onPreview: (row: DayOnePrecedentRow) => void;
  /** Org for the firm workflow-status dropdown (MOR-736). */
  orgId: Id<"organizations"> | null;
  /** Set / clear the firm workflow status on one document (MOR-736). */
  onWorkflowStatusChange: (
    precedentId: Id<"jobPrecedents">,
    statusId: Id<"taskStatuses"> | null,
  ) => void;
  onDelete: (precedentId: Id<"jobPrecedents">, name: string) => void;
  /**
   * "Generate draft email" (MOR-529 P4): the mail agent drafts a cover
   * email with the document attached, for approval in Mail → For Review.
   * `null` disables the action (no active job mailbox).
   */
  onSendWithMorgan: ((precedentId: Id<"jobPrecedents">) => void) | null;
  /** Rows with a Morgan draft awaiting review — badge under Actions. */
  pendingDraftPrecedentIds: ReadonlySet<string>;
  /** Open the contact detail panel for the row's linked contact. */
  onOpenContact: (row: DayOnePrecedentRow) => void;
  /** Renders the per-row download menu (kept in the page module to
   *  avoid pulling letterhead/branding logic into this thin component). */
  renderDownloadMenu: (row: DayOnePrecedentRow) => React.ReactNode;
}

export function getMeta(
  table: TanStackTable<DayOnePrecedentRow>,
): DayOneTableMeta {
  return table.options.meta as DayOneTableMeta;
}
